import { Button, MenuItem, Select, TextField } from "@mui/material";
import React, { ReactElement, useEffect, useState } from "react";

import Modal from "../components/modal/modal";
import TemplateSelector from "../components/selectors/template-selector";
import { CreateLayoutInput } from "../generated/gql/graphql";
import useWorkspace from "../hooks/use-workspace";

export type CreateLayoutModalProps = {
  open: boolean;
  initialProjectId?: string;
  initialWorkspaceId?: string;

  submit: (input: CreateLayoutInput) => void | Promise<void>;
  onClose(): void;
};

export default function CreateLayoutModal(
  props: CreateLayoutModalProps,
): ReactElement {
  const { open, initialProjectId, initialWorkspaceId, submit, onClose } = props;

  const { workspaces } = useWorkspace();

  const writebleWorkspaces = workspaces?.filter((ws) => !ws.readOnly);

  const [name, setName] = useState<string>("");
  const [projects, setProjects] = useState<
    { id: string; name: string | null }[] | undefined
  >();
  const [workspaceId, setWorkspaceId] = useState<string>(
    initialWorkspaceId ?? "",
  );
  const [projectId, setProjectId] = useState<string>(initialProjectId ?? "");
  const [templateId, setTemplateId] = useState<string | null>(null);

  useEffect(() => {
    setProjectId(initialProjectId ?? "");
    setWorkspaceId(initialWorkspaceId ?? "");
  }, [initialProjectId, initialWorkspaceId]);

  useEffect(() => {
    const currentWorkspace = workspaces?.find((ws) => ws.id === workspaceId);

    setProjects(currentWorkspace?.projects ?? undefined);
  }, [workspaceId, workspaces]);

  async function onSubmit(): Promise<void> {
    await submit({
      projectId,
      name,
      ...(templateId ? { templateId } : {}),
    });
    onClose();
  }

  // Validate inputs
  const nameTooShort = name.length < 3;
  const noProjectSelected = !projectId;
  const submitDisabled = nameTooShort || noProjectSelected;

  return (
    <Modal
      open={open}
      headline="New Layout"
      onClose={() => onClose()}
      className="h-fit max-w-xl"
    >
      <>
        <div className="mb-[40px]">
          <div className="font-bold text-[16px] mb-[6px] text-[#4A4A4A]">
            Name your Layout
          </div>
          <TextField
            sx={{
              "& .MuiFormHelperText-root": {
                marginLeft: 0,
              },
            }}
            type="text"
            size="small"
            fullWidth
            variant="outlined"
            error={nameTooShort}
            defaultValue={name}
            onChange={(e) => setName(e.target.value)}
            helperText="Aim for a unique name (min. 3 characters)"
          />
        </div>
        <div className="mb-[20px]">
          <div className="font-bold text-[16px] mb-[6px] text-[#4A4A4A]">
            Create in Workspace
          </div>
          <Select
            size="small"
            className="border-0 text-left text-[14px] w-[50%]"
            value={workspaceId || ""}
            onChange={(e) => {
              setWorkspaceId(e.target.value as string);
              setProjectId("");
            }}
            error={!workspaceId}
          >
            {workspaces?.map((ws) => (
              <MenuItem key={ws.id} value={ws.id}>
                {ws.name}
              </MenuItem>
            ))}
          </Select>
        </div>
        {projects && (
          <div className="mb-[20px]">
            <div className="font-bold text-[16px] mb-[6px] text-[#4A4A4A]">
              Create in Project
            </div>
            <Select
              size="small"
              className="border-0 text-left text-[14px] w-[50%]"
              value={projectId || ""}
              onChange={(e) => setProjectId(e.target.value as string)}
              error={!projectId}
            >
              {projects?.map((ws) => (
                <MenuItem key={ws.id} value={ws.id}>
                  {ws.name}
                </MenuItem>
              ))}
            </Select>
          </div>
        )}
        <TemplateSelector
          templateId={templateId}
          title="Create from Template"
          onTemplateIdChange={setTemplateId}
        />
        <div className="flex flex-row justify-end pt-6 border-t border-1 border-[#cbd5e1] mx-[-36px] px-[36px] mt-[36px]">
          <Button
            variant="outlined"
            className="text-[#DD2127] text-[18px] py-[8px] border-[#DD2127] hover:border-[#DD2127] !rounded-xl"
            onClick={() => onSubmit()}
            disabled={submitDisabled}
          >
            Create Layout
          </Button>
        </div>
      </>
    </Modal>
  );
}
